import React, { useEffect, useState, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { usePermisos } from '../../context/PermisosContext';
import { Search } from 'lucide-react';
import VisitasTable from './VisitasTable';
import VisitaEditar from './VisitaEditar';
import VisitaCrear from './VisitaCrear';
import { getApiUrl } from '../../utils/apiConfig';


const VisitasContent = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [filter, setFilter] = useState('');
  const [tipoVisitaFilter, setTipoVisitaFilter] = useState('');
  const [estadoOfertaFilter, setEstadoOfertaFilter] = useState('');
  const [segmentoFilter, setSegmentoFilter] = useState('');
  const [potencialClienteFilter, setPotencialClienteFilter] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [modalIsOpen, setModalIsOpen] = useState(false);
  const [selectedVisita, setSelectedVisita] = useState(null);
  const [crearVisitaIsOpen, setcrearVisitaIsOpen] = useState(false);
  const [nuevaVisita, setNuevaVisita] = useState(null);
  const [currentPage, setCurrentPage] = useState(0);
  

  const { user, visitas, setVisitas, loadingPermisos, loadingVisitas, comercialIdsPermitidos, refreshVisitas, tieneAccesoVisitas } = usePermisos();

  const tipoVisitaOptions = Array.from(new Set(visitas.map(v => v.TipoVisita).filter(Boolean)));
  const estadoOfertaOptions = Array.from(new Set(visitas.map(v => v.EstadoOferta).filter(Boolean)));
  const segmentoOptions = Array.from(new Set(visitas.map(v => v.Segmento).filter(Boolean)));
  const potencialClienteOptions = Array.from(new Set(visitas.map(v => v.PotencialCliente).filter(Boolean)));

  const filtrosActivos =
    filter.trim() !== '' ||
    searchTerm.trim() !== '' ||
    tipoVisitaFilter !== '' ||
    estadoOfertaFilter !== '' ||
    segmentoFilter !== '' ||
    potencialClienteFilter !== ''
  ;

  const filteredVisitas = visitas.filter(v => {
    const search = searchTerm.toLowerCase();
    // Unir todos los valores de la visita en un solo string
    const allValues = Object.values(v).join(' ').toLowerCase();
    const textMatch = !searchTerm || allValues.includes(search);
    return (
      textMatch &&
      (!tipoVisitaFilter || v.TipoVisita === tipoVisitaFilter) &&
      (!estadoOfertaFilter || v.EstadoOferta === estadoOfertaFilter) &&
      (!segmentoFilter || v.Segmento === segmentoFilter) &&
      (!potencialClienteFilter || v.PotencialCliente === potencialClienteFilter)
    );
  });

  const openModal = (visita) => {
    setSelectedVisita({ ...visita });
    setModalIsOpen(true);
  };

  const closeModal = () => {
    setModalIsOpen(false);
    setSelectedVisita(null);
  };

  const openCrearModal = () => {
    setNuevaVisita({
      IdComercial: '',
      IdCliente: '',
      FechaVisita: '',
      IdTipoVisita: '',
      IdSegmento: '',
      PotencialCliente: '',
      Comentarios: '',
      Oferta: '',
      ImporteAproxOferta: '',
      EstadoOferta: '',
      FechaVisita: new Date().toISOString().slice(0, 10),
      Direccion: '',
      LatLong: '',
      Latitud: null,
      Longitud: null,
    });
    setcrearVisitaIsOpen(true);
  };

  const closeCrearModal = () => {
    setcrearVisitaIsOpen(false);
    setNuevaVisita(null);
  };

  const handleSave = async () => {
    if (!selectedVisita?.IdOferta) return null;
    try {
      const res = await fetch(`${getApiUrl('/api/ActividadComercialPUT/actividad-comercial')}/${selectedVisita.IdOferta}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          IdComercial: selectedVisita.IdComercial,
          IdCliente: selectedVisita.IdCliente,
          FechaVisita: selectedVisita.FechaVisita,
          IdTipoVisita: selectedVisita.IdTipoVisita,
          IdSegmento: selectedVisita.IdSegmento,
          PotencialCliente: selectedVisita.PotencialCliente,
          Comentarios: selectedVisita.Comentarios,
          Oferta: selectedVisita.Oferta,
          ImporteAproxOferta: selectedVisita.ImporteAproxOferta,
          EstadoOferta: selectedVisita.EstadoOferta,
          FechaCreacionRegistro: selectedVisita.FechaCreacionRegistro,
          FechaUltModificacionRegistro: new Date().toISOString(),
          ObservacionesC: selectedVisita.ObservacionesC,
          IdObra: selectedVisita.IdObra,
          Direccion: selectedVisita.Direccion,
          LatLong: selectedVisita.LatLong,
        }),
      });
      if (res.ok) {
        return { ...selectedVisita };
      } else {
        throw new Error('Error al guardar los cambios');
      }
    } catch (err) {
      console.error('Error al guardar:', err);
      throw err;
    }
  };

  const handleUpdateVisita = (updatedVisita) => {
    setVisitas((prev) => prev.map(v => v.IdOferta === updatedVisita.IdOferta ? { ...v, ...updatedVisita } : v));
    closeModal();
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && filter.trim() !== '') {
      setSearchTerm(filter);
      setCurrentPage(0);
    }
  };

  const handleSearch = () => {
    if (filter.trim() !== '') {
      setSearchTerm(filter);
      setCurrentPage(0);
    }
    // Si está vacío, no hace nada
  };

  // � Detectar parámetro de URL para abrir una visita específica
  useEffect(() => {
    const visitaId = searchParams.get('visita');
    if (visitaId && visitas.length > 0 && !loadingVisitas) {
      const visitaEncontrada = visitas.find(v => String(v.IdOferta) === String(visitaId));
      if (visitaEncontrada) {
        setSelectedVisita({ ...visitaEncontrada });
        setModalIsOpen(true);
        // Limpiar el parámetro de la URL después de abrir
        searchParams.delete('visita');
        setSearchParams(searchParams, { replace: true });
      }
    }
  }, [searchParams, visitas, loadingVisitas]);

  // �🔐 Si el usuario NO tiene acceso a visitas (DESPUÉS de todos los hooks y funciones)
  if (!tieneAccesoVisitas && !loadingPermisos) {
    return (
      <div className="min-h-screen w-full flex items-center justify-center p-4">
        <div className="text-center border rounded-xl p-8">
          <div className="mb-4">
            <svg className="w-20 h-20 mx-auto text-red-400 sm:w-24 sm:h-24" fill="currentColor" viewBox="0 0 20 20" aria-hidden="true">
              <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
            </svg>
          </div>
          <h2 className="text-2xl font-bold text-gray-800 mb-3">Acceso Denegado</h2>
          <p className="text-gray-600 mb-2 text-sm">
            Tu usuario no tiene permisos para acceder a la sección de <strong>Visitas</strong>.
          </p>
          <p className="text-xs text-gray-500 mb-6">
            Si crees que esto es un error, contacta con el administrador del sistema.
          </p>
          <button
            onClick={() => window.history.back()}
            className="px-3 py-1.5 rounded-lg text-red-600 border bg-red-50 hover:bg-red-100 border-red-200 text-sm font-medium transition-colors duration-200"
          >
            Volver al Dashboard
          </button>
        </div>
      </div>

    );
  }

  // Si el usuario no está cargado, mostrar loading
  if (!user && loadingPermisos) {
    return (
      <div className="flex flex-col justify-center items-center py-32">
        <div className="loader border-t-4 border-blue-500 rounded-full w-12 h-12 animate-spin mb-4"></div>
        <p className="text-gray-600">Cargando información del usuario...</p>
      </div>
    );
  }

  return (
    <>
      {selectedVisita ? (
        <VisitaEditar
          visita={selectedVisita}
          setVisita={setSelectedVisita}
          onSave={handleSave}
          onUpdateVisita={handleUpdateVisita}
          onClose={closeModal}
          user={user}
          refreshVisitas={refreshVisitas}
          comercialIdsPermitidos={comercialIdsPermitidos}
        />
      ) : crearVisitaIsOpen ? (
        <VisitaCrear
          visita={nuevaVisita}
          setVisita={setNuevaVisita}
          onClose={closeCrearModal}
          user={user}
          comercialIdsPermitidos={comercialIdsPermitidos}
          refreshVisitas={refreshVisitas}
        />
      ) : (
        // <div className="p-4 sm:p-6 space-y-6 bg-gray-50 min-h-screen">
      <div className="p-4 sm:p-6 lg:p-6  bg-gray-50 no-select min-h-screen">
        <div className="max-w-7xl mx-auto">

        {/* Header */}
        <div className="flex flex-col sm:flex-row gap-4 my-4 sm:gap-0 sm:justify-between sm:items-center">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-800">Visitas</h1>
            <p className="text-gray-600 mt-1 text-sm sm:text-base">Gestión de visitas comerciales</p>
          </div>
          <div className="flex items-center gap-3 bg-white px-3 sm:px-4 py-2 sm:py-3 rounded-xl shadow-sm border hover:shadow-lg hover:border-blue-200 transition-all duration-300 cursor-pointer">
            <div className="w-7 h-7 sm:w-8 sm:h-8 bg-gradient-to-r from-blue-400 to-purple-400 rounded-full flex items-center justify-center text-white font-semibold text-xs sm:text-sm">
              {user?.name?.charAt(0)?.toUpperCase() || 'U'}
            </div>
            <div className="text-right">
              <p className="font-medium text-gray-800 text-xs sm:text-sm md:text-base truncate max-w-[100px] sm:max-w-[150px] md:max-w-none">
                {user?.name || 'Usuario'}
              </p>
            </div>
          </div>
        </div>

          {/* Filtros y acciones */}
          <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-4 sm:p-4 my-4">
            <div className="flex flex-col gap-4">
              {/* Filtros principales */}
              <div className="flex flex-col sm:flex-row sm:flex-wrap gap-3 sm:gap-4 items-stretch sm:items-end flex-1">
                {/* Búsqueda */}
                <div className="flex flex-col w-full sm:w-auto">
                  <label className="text-sm font-medium text-gray-700 mb-1">Buscar</label>
                  <div className="flex w-full sm:w-64">
                    <input
                      type="text"
                      placeholder="Buscar en todas las columnas..."
                      value={filter}
                      onChange={(e) => setFilter(e.target.value)}
                      onKeyPress={handleKeyPress}
                      className="px-3 sm:px-4 py-2 border border-gray-200 rounded-l-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 flex-1 sm:flex-none border-r-0 text-sm"
                    />
                    <button
                      onClick={handleSearch}
                      className="px-2 sm:px-3 py-1.5 bg-blue-600 text-white rounded-r-xl hover:bg-blue-700 transition-all duration-200 shadow-sm border border-blue-600 border-l-0 flex-shrink-0"
                      title="Buscar"
                    >
                      <Search size={16} className="sm:w-[18px] sm:h-[18px]" />
                    </button>
                  </div>
                </div>

                {/* Filtros dropdown - Grid responsive */}
                <div className="grid grid-cols-2 sm:flex gap-3 sm:gap-4 w-full sm:w-auto">
                  <DropdownGroup 
                    label="Tipo Visita" 
                    options={tipoVisitaOptions} 
                    value={tipoVisitaFilter} 
                    onChange={value => {
                      setTipoVisitaFilter(value);
                      setCurrentPage(0);
                    }}
                    placeholder="Todas" 
                  />

                  <DropdownGroup 
                    label="Estado Oferta" 
                    options={estadoOfertaOptions} 
                    value={estadoOfertaFilter} 
                    onChange={value => {
                      setEstadoOfertaFilter(value);
                      setCurrentPage(0);
                    }}
                    placeholder="Todas" 
                  />

                  <DropdownGroup 
                    label="Segmento" 
                    options={segmentoOptions} 
                    value={segmentoFilter} 
                    onChange={value => {
                      setSegmentoFilter(value);
                      setCurrentPage(0);
                    }}
                    placeholder="Todos" 
                  />

                  <DropdownGroup 
                    label="Potencial Cliente" 
                    options={potencialClienteOptions} 
                    value={potencialClienteFilter} 
                    onChange={value => {
                      setPotencialClienteFilter(value);
                      setCurrentPage(0);
                    }} 
                    placeholder="Todos" 
                  />
                </div>

                {/* Botón limpiar filtros */}
                {filtrosActivos && (
                  <div className="flex justify-center sm:justify-start w-full sm:w-auto">
                    <button
                      onClick={() => {
                        setFilter('');
                        setSearchTerm('');
                        setTipoVisitaFilter('');
                        setEstadoOfertaFilter('');
                        setSegmentoFilter('');
                        setPotencialClienteFilter('');
                        setCurrentPage(0);
                      }}
                      className="px-4 py-2 bg-red-100 text-red-700 rounded-xl hover:bg-red-200 transition-all duration-200 border border-red-200 text-sm w-full sm:w-auto"
                      title="Limpiar filtros"
                    >
                      Limpiar filtros
                    </button>
                  </div>
                )}
              {/* Acciones y contador */}
              <div className='justify-between'>
                <div className="grid grid-cols-2 sm:flex gap-3 sm:gap-4 w-full sm:w-auto sm:items-center justify-end pt-3 sm:pt-0 border-t sm:border-t-0">
                  {/* Contador */}
                  <div className="flex justify-center sm:justify-start">
                    <span className="px-3 py-2 bg-green-100 text-green-800 rounded-xl text-sm font-semibold border border-green-200 w-full sm:w-auto text-center">
                      {filteredVisitas.length} visitas
                    </span>
                  </div>

                  {/* Botón nueva visita */}
                  <button
                    onClick={openCrearModal}
                    className="px-4 py-2 bg-blue-600 text-white rounded-xl text-sm font-semibold hover:bg-blue-700 transition-all duration-200 shadow-sm border border-blue-600 w-full sm:w-auto"
                    title="Crear nueva visita"
                  >
                    + Nueva Visita
                  </button>
                </div>
              </div>


              </div>


            </div>
          </div>

          {loadingPermisos || loadingVisitas ? (
            <div className="flex flex-col justify-center items-center py-32">
              <div className="loader border-t-4 border-red-500 rounded-full w-12 h-12 animate-spin mb-4"></div>
              <p className="text-gray-600">Cargando visitas...</p>
            </div>
          ) : visitas.length === 0 ? (
            <div className='flex justify-center mt-5'>
              <p className="text-center mt-5 bg-gray-200 rounded-xl text-gray-800 border-gray-300 w-max p-2">No se encontraron visitas en la base de datos.</p>
            </div>
          ) : filteredVisitas.length > 0 ? (
            <VisitasTable 
              visitas={filteredVisitas} 
              openModal={openModal} 
              setCurrentPage={setCurrentPage} 
              currentPage={currentPage} 
            />
          ) : (
            <div className='flex justify-center mt-5'>
              <p className="text-center mt-5 bg-gray-200 rounded-xl text-gray-800 border-gray-300 w-max p-2">Ninguna visita coincide con el filtro.</p>
            </div>
          )}
        </div>
      </div>
      
      )}
    </>
  );
};

function DropdownGroup({ label, options, value, onChange, placeholder }) {
  return (
    <div className="flex flex-col min-w-0 w-full sm:min-w-[140px] sm:w-auto">
      <label className="text-xs sm:text-sm font-medium text-gray-700 mb-1 truncate">{label}</label>
      <DropdownFilter options={options} value={value} onChange={onChange} placeholder={placeholder} />
    </div>
  );
}

function DropdownFilter({ options, value, onChange, placeholder }) {
  const [open, setOpen] = useState(false);
  const ref = useRef();

  useEffect(() => {
    if (!open) return;
    const handleClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [open]);

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        className="w-full px-3 sm:px-4 py-1.5 sm:py-2 border border-gray-200 rounded-xl bg-white text-left flex justify-between items-center hover:border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
        onClick={() => setOpen(o => !o)}
      >
        <span className="truncate block max-w-[70%] sm:max-w-[80%] text-xs sm:text-sm" title={value || placeholder}>
          {value || placeholder}
        </span>
        <svg 
          className={`w-3 h-3 sm:w-4 sm:h-4 ml-1 sm:ml-2 text-gray-500 flex-shrink-0 transition-transform duration-200 ${open ? 'rotate-180' : ''}`} 
          fill="none" 
          stroke="currentColor" 
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>
      {open && (
        <ul className="absolute z-30 mt-1 min-w-full w-max bg-white border border-gray-200 rounded-xl shadow-lg max-h-60 overflow-auto break-words">
          <li
            className={`cursor-pointer px-4 py-2 text-sm hover:bg-blue-50 transition-colors ${!value ? 'bg-blue-50 text-blue-700 font-medium' : ''}`}
            onClick={() => { onChange(''); setOpen(false); }}
          >
            {placeholder}
          </li>
          {options.map(opt => (
            <li
              key={opt}
              className={`cursor-pointer px-4 py-2 text-sm hover:bg-blue-50 hover:text-blue-700 transition-colors ${value === opt ? 'bg-blue-50 text-blue-700 font-medium' : ''}`}
              onClick={() => { onChange(opt); setOpen(false); }}
            >
              {opt}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export default VisitasContent;
